import "@/App.css";
import { useEffect, useRef } from "react";
import { BrowserRouter, Routes, Route, useLocation, useNavigate } from "react-router-dom";
import { useSession } from "@/hooks/useSession";
import { purgeCacheIfStale } from "@/lib/me";
import { initAnalytics, trackPage, isExcluded, getConsent, deactivate, activate } from "@/lib/analytics";
import Home from "@/pages/Home";
import Showcase from "@/pages/Showcase";
import Questionnaire from "@/pages/Questionnaire";
import Done from "@/pages/Done";
import Login from "@/pages/Login";
import AuthCallback from "@/pages/AuthCallback";
import FacePhoto from "@/pages/FacePhoto";
import BodyPhoto from "@/pages/BodyPhoto";
import EmailScreen from "@/pages/EmailScreen";
import Teasing from "@/pages/Teasing";
import Loading from "@/pages/Loading";
import Preview from "@/pages/Preview";
import Coach from "@/pages/Coach";
import Compte from "@/pages/Compte";
import Accueil from "@/pages/Accueil";
import Admin from "@/pages/Admin";
import Creator from "@/pages/Creator";
import { MentionsLegales, CGV, Confidentialite } from "@/pages/Legal";
import PaymentReturn from "@/pages/PaymentReturn";
import TestModeOverlay from "@/components/revanche/TestModeOverlay";

// Rejoue l'intro (qui re-route via /api/me) quand l'app revient au premier plan après > 30 s.
function useForegroundReentry() {
    const navigate = useNavigate();
    const location = useLocation();
    const hiddenAt = useRef(null);
    useEffect(() => {
        const onVis = () => {
            if (document.visibilityState === "hidden") {
                hiddenAt.current = Date.now();
            } else if (hiddenAt.current && Date.now() - hiddenAt.current > 30000) {
                hiddenAt.current = null;
                const p = window.location.pathname;
                // On ne perturbe pas l'admin, le mode créateur, ni un paiement en cours.
                if (!p.startsWith("/admin") && !p.startsWith("/createur") && !p.startsWith("/payment")) {
                    navigate("/", { replace: true });
                }
            }
        };
        document.addEventListener("visibilitychange", onVis);
        return () => document.removeEventListener("visibilitychange", onVis);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [location.pathname]);
}

function AppRouter() {
    const location = useLocation();
    useForegroundReentry();
    // Suivi de page + mise en pause sur les écrans jamais suivis (admin, mode créateur).
    useEffect(() => {
        if (getConsent() !== "granted") return;
        if (isExcluded()) deactivate();
        else { activate(); trackPage(location.pathname); }
    }, [location.pathname]);
    if (location.hash?.includes("session_id=")) {
        return <AuthCallback />;
    }
    return (
        <>
            <Routes>
            <Route path="/" element={<Home />} />
            <Route path="/analyse" element={<Questionnaire />} />
            <Route path="/photo" element={<FacePhoto />} />
            <Route path="/photo-corps" element={<BodyPhoto />} />
            <Route path="/email" element={<EmailScreen />} />
            <Route path="/teasing" element={<Teasing />} />
            <Route path="/analyse-en-cours" element={<Loading />} />
            <Route path="/apercu" element={<Preview />} />
            <Route path="/solution" element={<Preview />} />
            <Route path="/coach" element={<Coach />} />
            <Route path="/compte" element={<Compte />} />
            <Route path="/accueil" element={<Accueil />} />
            <Route path="/admin" element={<Admin />} />
            <Route path="/createur" element={<Creator />} />
            <Route path="/createur/:token" element={<Creator />} />
            <Route path="/mentions-legales" element={<MentionsLegales />} />
            <Route path="/cgv" element={<CGV />} />
            <Route path="/confidentialite" element={<Confidentialite />} />
            <Route path="/payment/success" element={<PaymentReturn />} />
            <Route path="/payment/cancel" element={<PaymentReturn cancel />} />
            <Route path="/connexion" element={<Login />} />
            <Route path="/analyse-terminee" element={<Done />} />
            <Route path="/composants" element={<Showcase />} />
            <Route path="*" element={<Home />} />
            </Routes>
            <TestModeOverlay />
        </>
    );
}

function App() {
    useEffect(() => { purgeCacheIfStale(); initAnalytics(); }, []);
    useSession();
    return (
        <div className="App">
            <BrowserRouter>
                <AppRouter />
            </BrowserRouter>
        </div>
    );
}

export default App;
